import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { billing } from "@/lib/billing";
import { pick, asList, fmtDateTime } from "@/lib/admin-utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, UserCheck, Clock, Zap, ClipboardList, Briefcase, ScrollText } from "lucide-react";

export function AdminDashboard() {
  const { data: kpi, isLoading } = useQuery({
    queryKey: ["admin-kpi"],
    queryFn: async () => (await api.get("/admin/kpi")).data,
  });
  const { data: subs } = useQuery({
    queryKey: ["admin-subscriptions"],
    queryFn: async () => (await billing.get("/admin/subscriptions")).data,
  });
  const { data: audit } = useQuery({
    queryKey: ["admin-audit"],
    queryFn: async () => (await api.get("/admin/audit")).data,
  });

  const cards = [
    { label: "Utenti totali", value: pick(kpi, ["users_total", "total_users", "users"]), icon: Users, testid: "kpi-users" },
    { label: "Professionisti", value: pick(kpi, ["pros_total", "total_professionals", "professionals", "pros"]), icon: UserCheck, testid: "kpi-pros" },
    { label: "In attesa", value: pick(kpi, ["pending_pros", "pros_pending", "pending"]), icon: Clock, testid: "kpi-pending" },
    { label: "WhooNow Pro attivi", value: subs?.pro_active_count ?? pick(kpi, ["pro_subscriptions", "active_subscriptions"]), icon: Zap, testid: "kpi-pro-subs" },
    { label: "Richieste attive", value: pick(kpi, ["requests_active", "active_requests", "open_requests"]), icon: ClipboardList, testid: "kpi-requests" },
    { label: "Lavori completati", value: pick(kpi, ["jobs_completed", "completed_jobs", "jobs_done"]), icon: Briefcase, testid: "kpi-jobs" },
  ];

  const auditList = asList(audit).slice(0, 10);

  return (
    <div className="px-4 sm:px-6 pt-5 space-y-6" data-testid="admin-dashboard">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Dashboard Admin</h1>

      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">{[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-24 rounded-3xl" />)}</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="kpi-grid">
          {cards.map(({ label, value, icon: Icon, testid }) => (
            <div key={testid} data-testid={testid} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4">
              <Icon className="w-5 h-5 text-whoo mb-2" />
              <p className="font-heading text-2xl font-bold text-slate-900">{value ?? "—"}</p>
              <p className="text-xs text-slate-500">{label}</p>
            </div>
          ))}
        </div>
      )}

      <section className="space-y-3">
        <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-slate-400" /> Audit log recente
        </h2>
        {auditList.length ? (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="audit-list">
            {auditList.map((a, i) => (
              <div key={a.id || i} className="p-3.5 text-sm flex items-center justify-between gap-3">
                <span className="text-slate-700">{pick(a, ["action", "event", "type"], "Evento")} — {pick(a, ["target", "target_id", "user_id"], "")}</span>
                <span className="text-xs text-slate-400 shrink-0">{fmtDateTime(pick(a, ["created_at", "timestamp", "at"]))}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500" data-testid="audit-empty">Nessuna operazione registrata.</p>
        )}
      </section>
    </div>
  );
}
